<template>
  <div class="main mt-2 rounded" :style="outsideBorder">
    <v-alert
      v-if="errorMessage"
      class="mb-4"
      type="error"
      closable
      @click:close="emit('updateError', '')"
    >
      {{ $t(errorMessage) }}
    </v-alert>
    <v-data-table-virtual
      v-resize="onResize"
      :height="height"
      :headers="headers"
      :items="modifiedItems"
      :loading="loading"
      :data-loading="loading ? 'true' : undefined"
      :fixedHeader="true"
      :hideDefaultBody="!isGrid"
      :hideDefaultHeader="!isGrid"
    >
      <template #item="{ item, itemRef, columns }">
        <tr :ref="itemRef">
          <td
            v-for="(column, index) in columns"
            :key="column.key ?? index"
            :style="{
              backgroundColor: item.rowColor?.background || undefined,
              width: column.width,
              minWidth: (column.minWidth ?? column.width) as string | undefined,
            }"
            :data-column="column.key"
          >
            <TableColumnActions
              v-if="usedColumnActions.isActionsColumn(column.key as string, index)"
              v-bind="usedColumnActions.componentProps(column.key as string, index)"
              :item="item.srcItem"
            >
              <slot :name="(column.key as string).toLowerCase()" v-bind="slotParams(item.srcItem, item[column.key as string])">
                {{ item[column.key as string] }}
              </slot>
            </TableColumnActions>
            <slot v-else :name="(column.key as string).toLowerCase()" v-bind="slotParams(item.srcItem, item[column.key as string])">
              {{ item[column.key as string] }}
            </slot>
          </td>
        </tr>
      </template>

      <template #top>
        <div
          v-if="$slots.top || allowSwitchMode"
          class="d-flex justify-content-center flex-column flex-md-row flex-lg-row flex-xl-row toolbox-padding"
          :class="mdAndUp ? 'mdAndUp' : undefined"
        >
          <slot name="top">
            <v-switch
              v-if="allowSwitchMode"
              v-model="isGrid"
              class="pl-3"
              :label="$t('tableGrid.show_as_grid')"
              hideDetails
            />
          </slot>
        </div>

        <v-expansion-panels v-if="!isGrid" class="mb-4">
          <template v-for="(item, i) in modifiedItems" :key="`item_${i}`">
            <slot name="expansion" :item="item">
              <v-expansion-panel>
                <v-expansion-panel-title
                  :style="item.rowColor?.background ? `background-color: ${item.rowColor.background}` : undefined"
                >
                  <MemberInfoHeader :data="item.srcItem" :columnActions="columnActions">
                    <template v-if="$slots.cardAvatar" #avatar="avatarProps">
                      <slot name="cardAvatar" v-bind="avatarProps" />
                    </template>
                  </MemberInfoHeader>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="pt-4">
                  <MemberInfo
                    :data="item.srcItem"
                    :properties="properties"
                    :propertyTitles="headerTitles || {}"
                    :noHeader="true"
                    :columnActions="columnActions"
                  />
                </v-expansion-panel-text>
              </v-expansion-panel>
            </slot>
          </template>
        </v-expansion-panels>
      </template>
    </v-data-table-virtual>
    <slot name="bottom" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';

import { TableGridItem, slotParams } from '@/models/table-grid-interface';

import { useProperties } from '@/composables';
import { ColorScheme } from '@/utils/conditional-colors';
import { useDisplay } from 'vuetify';

import { TableColumnActionsSchema, useTableColumnActions } from '@/composables/useTableColumnActions';
import MemberInfo from './MemberInfo.vue';
import MemberInfoHeader from './MemberInfoHeader.vue';
import TableColumnActions from './TableColumnActions.vue';

type TableGridVirtualProps = {
  properties: string[],
  items: TableGridItem[],
  headerTitles?: Record<string, string>;
  loading?: boolean;
  autoSwitchMode?: boolean;
  allowSwitchMode?: boolean;
  drawBorder?: boolean;
  errorMessage?: string;
  columnActions?: TableColumnActionsSchema;
  height?: string | number;
};

const props = withDefaults(defineProps<TableGridVirtualProps>(), {
  headerTitles: undefined,
  errorMessage: undefined,
  drawBorder: undefined,
  columnActions: undefined,
  height: '70vh',
});

const emit = defineEmits<{
  (event: 'updateError', result: string): void,
}>();

const { mdAndUp } = useDisplay();
const usedColumnActions = useTableColumnActions(props.columnActions);
const refHeadersOverride = toRef( () => props.headerTitles || {} );
const { propertyTranslations } = useProperties(props.properties, [], refHeadersOverride);

const isGrid = ref(true);

const nothingFound = computed(() => props.items.length === 0 && !props.loading);

const headers = computed(() => props.properties.map((property) => ({
  title: propertyTranslations.value?.[property] || property,
  key: property,
  value: property,
  sortable: false,
})));

const outsideBorder = computed(() => {
  const doDraw = props.drawBorder === undefined ? isGrid.value : props.drawBorder;
  return doDraw ? 'border: 2px solid #e0e0e0;' : undefined;
});

const modifiedItems = computed(() => props.items.map((dataItem) => {
  type InternalItem = Record<string, string> &
    {
      rowColor?: ColorScheme,
      srcItem: TableGridItem,
    };

  const item: InternalItem  = {
    srcItem: dataItem,
  } as InternalItem;

  props.properties.forEach((property) => {
    item[property] = String(dataItem[property]?.presentable || '');
  });

  item['rowColor'] = dataItem['rowColor'];

  return item;
}));

function onResize() {
  if (props.autoSwitchMode) isGrid.value = !(window.innerWidth < 800);
}
</script>

<style lang="scss" scoped>
.main {
  padding-top: 1em;
  padding-bottom: 1em;
}

.toolbox-padding {
  padding-right: 1em;
  padding-bottom: 1em;
  padding-left: 1em;
}

.mdAndUp {
  gap: 1em;
  padding-top: 0;
}

</style>
